import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import OfferingForm from '../../Components/Offerings/OfferingForm';
import DeleteConfirmationModal from '../../Components/Offerings/DeleteConfirmationModal';
import { getOfferingById, updateOffering, deleteOffering } from '../../api/offerings';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import OfferingProductsManager from '../../Components/Offerings/OfferingProductsManager';
import '../AdventureShared.css';
import '../CreateOfferingPage/CreateOfferingPage.css';
import './EditOfferingPage.css';


export default function EditOfferingPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [offering, setOffering] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [toast, setToast] = useState({ message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 5000);
  };

  useEffect(() => {
    const fetchOffering = async () => {
      try {
        const data = await getOfferingById(id);
        setOffering(data);
      } catch (err) {
        console.error("Failed to fetch offering", err);
        alert("Failed to load offering details.");
        navigate('/facilitate/offerings');
      } finally {
        setIsLoading(false);
      }
    };
    fetchOffering();
  }, [id, navigate]);

  const handleSubmit = async (payload) => {
    setIsSubmitting(true);
    try {
      await updateOffering(id, payload);
      navigate('/facilitate/offerings');
    } catch (err) {
      console.error("Failed to update offering", err);
      showToast("Failed to save changes. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOffering(id);
      setDeleteModalOpen(false);
      navigate('/facilitate/offerings');
    } catch (err) {
      console.error("Failed to delete offering", err);
      setDeleteModalOpen(false);
      showToast(err.message || "Failed to delete offering.", "error");
    }
  };

  return (
    <div className="EditOfferingPage">
      <div className="adventurePageContainer">
        <div className="headerContain">
          <Header />
        </div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain">
            <Sidebar />
          </div>

          <main className="adv-page-main animate-fade-in">
            {toast.message && (
              <div 
                style={{
                  position: 'fixed',
                  top: '90px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: toast.type === 'error' ? '#1c0e0e' : '#0e1b1b',
                  border: `1.5px solid ${toast.type === 'error' ? '#dc3545' : '#48d1cc'}`,
                  color: '#ffffff',
                  padding: '12px 24px',
                  borderRadius: '30px',
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                  zIndex: 10000,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{ color: toast.type === 'error' ? '#dc3545' : '#48d1cc', fontWeight: 800 }}>
                  {toast.type === 'error' ? '⚠️' : '✓'}
                </span>
                {toast.message}
              </div>
            )}
            <div className="off-create-middle">
              <div className="off-create-header off-edit-header">
                <div className="off-edit-header__left">
                  <button className="off-back-btn" onClick={() => navigate('/facilitate/offerings')}>
                    ← Back to Offerings
                  </button>
                  <h1 className="off-create-title">Edit Offering</h1>
                </div>

                {!isLoading && (
                  <button className="off-btn-delete-header" onClick={() => setDeleteModalOpen(true)}>
                    Delete Offering
                  </button>
                )}
              </div>

              {isLoading ? (
                <div className="off-create-loader">
                  <div className="off-create-spinner"></div>
                  <p>Loading offering details...</p>
                </div>
              ) : (
                <>
                  <div className="adv-tabs" style={{ display: 'flex', gap: '20px', marginBottom: '24px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <button 
                      type="button"
                      className={`adv-tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                      onClick={() => setActiveTab('details')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: activeTab === 'details' ? '#3bb3ae' : 'rgba(255,255,255,0.45)',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        padding: '10px 0',
                        borderBottom: activeTab === 'details' ? '2px solid #3bb3ae' : '2px solid transparent',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Offering Details
                    </button>
                    <button 
                      type="button"
                      className={`adv-tab-btn ${activeTab === 'products' ? 'active' : ''}`}
                      onClick={() => setActiveTab('products')}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: activeTab === 'products' ? '#3bb3ae' : 'rgba(255,255,255,0.45)',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        padding: '10px 0',
                        borderBottom: activeTab === 'products' ? '2px solid #3bb3ae' : '2px solid transparent',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Bundled Products
                    </button>
                  </div>

                  {activeTab === 'details' ? (
                    <OfferingForm
                      initialData={offering}
                      isEdit={true}
                      isSubmitting={isSubmitting}
                      onSubmit={handleSubmit}
                      onCancel={() => navigate('/facilitate/offerings')}
                    />
                  ) : (
                    <OfferingProductsManager offeringId={id} />
                  )}
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        offeringTitle={offering?.title}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
